"use client";

import { Bookmark } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { sendJson } from "@/components/send-json/send-json";
import { useNotify } from "@/components/toaster/app-toaster";
import { Button } from "@/components/ui/button";
import { Link } from "@/i18n/link";
import type { ReactionKind } from "@/modules/blog-community";

type Props = { postId: string; kinds: readonly ReactionKind[]; counts: Record<ReactionKind, number>; mine: ReactionKind | null; saved: boolean; signedIn: boolean };

/** The reaction buttons (one choice per reader, pressed again to take it back, counts shown) and the "save for later" button. They need a signed-in reader. */
export function EngagementButtons({ postId, kinds, counts: first, mine: firstMine, saved: firstSaved, signedIn }: Props) {
  const t = useTranslations("publicBlog.engagement");
  const notify = useNotify();
  const [counts, setCounts] = useState(first);
  const [mine, setMine] = useState(firstMine);
  const [saved, setSaved] = useState(firstSaved);
  async function react(kind: ReactionKind) {
    const next = mine === kind ? null : kind;
    const result = await sendJson("PUT", "/api/blog/reactions", { postId, kind: next });
    if (!result.ok) return notify.error(t("error"));
    setCounts((c) => ({ ...c, ...(mine ? { [mine]: c[mine] - 1 } : {}), ...(next ? { [next]: c[next] + 1 } : {}) }));
    setMine(next);
  }
  async function save() {
    const result = await sendJson("PUT", "/api/blog/bookmarks", { postId, saved: !saved });
    if (result.ok) setSaved(!saved); else notify.error(t("error"));
  }
  return (
    <div className="flex flex-wrap items-center gap-2" title={signedIn ? undefined : t("signIn")}>
      {kinds.map((k) => <Button key={k} type="button" size="sm" variant={mine === k ? "secondary" : "outline"} disabled={!signedIn} aria-pressed={mine === k} aria-label={t(k)} onClick={() => react(k)}>{REACTION_EMOJI[k]} {counts[k]}</Button>)}
      {signedIn && <Link href="/bookmarks" className="ms-auto text-sm underline">{t("pageTitle")}</Link>}
      <Button type="button" size="sm" variant={saved ? "secondary" : "outline"} disabled={!signedIn} aria-pressed={saved} onClick={save} ><Bookmark aria-hidden />{saved ? t("saved") : t("save")}</Button>
    </div>
  );
}

/** The picture shown for each reaction. */
const REACTION_EMOJI: Record<ReactionKind, string> = { like: "👍", love: "❤️", clap: "👏", insight: "💡" };
